import { motion } from "framer-motion";
import { Globe, Smartphone, Cpu } from "lucide-react";

const points = [
  {
    icon: Globe,
    tint: "var(--sunflower)",
    title: "More external input than ever.",
    body: "Social media, AI, algorithms, peer pressure — all louder than the inner voice.",
  },
  {
    icon: Smartphone,
    tint: "var(--mint)",
    title: "Less inner quiet.",
    body: "Screen time has replaced reflection time — children hear everyone's voice but their own.",
  },
  {
    icon: Cpu,
    tint: "var(--violet)",
    title: "AI is shaping how children think.",
    body: "Algorithms decide what they see, who they follow, what they believe about themselves.",
  },
];

export default function WhyNow() {
  return (
    <section
      id="why-now"
      className="py-24 md:py-40 relative overflow-hidden"
      style={{ background: "var(--bg-2)" }}
      data-testid="why-now-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-8">
            <h2 className="mt-6 font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight text-[color:var(--ink)]">
              Why now?
              <br />
              <span className="italic font-serif-editorial" style={{ color: "var(--brand)" }}>
                The world your child is
              </span>{" "}
              <span className="italic font-serif-editorial">growing up in is different.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 md:col-start-9 text-[color:var(--ink)]/75 text-base leading-relaxed">
            Not different in degree. Different in kind. Three shifts, happening at once, are reshaping what it feels like to be a child.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {points.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="relative overflow-hidden border-2 p-8 md:p-10 min-h-[340px] flex flex-col justify-between paper-bg transition-transform duration-500 hover:-translate-y-1"
                style={{ borderColor: "var(--ink)" }}
                data-testid={`why-now-card-${i}`}
              >
                <div>
                  <span
                    className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-6"
                    style={{ background: p.tint }}
                  >
                    <Icon size={22} color="var(--ink)" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight text-[color:var(--ink)]">
                    {p.title}
                  </h3>
                </div>
                <p className="mt-6 text-[color:var(--ink)]/70 leading-relaxed">{p.body}</p>
                <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full opacity-40" style={{ background: p.tint }} aria-hidden />
              </motion.article>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="mt-16 md:mt-24 font-display text-3xl md:text-5xl leading-[1.1] tracking-tight text-[color:var(--ink)] max-w-[26ch]"
        >
          The question is not <em style={{ color: "var(--brand)" }}>can</em> they use technology — it is do they have an <em className="font-serif-editorial">inner compass</em> to guide them when they do?
        </motion.p>
      </div>
    </section>
  );
}
